import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useBanners } from "@/components/banner";
import { useUpdateProfileMutation } from "@/queries/hooks/profileHooks";
import type { Profile } from "@/schemas";
import { cn, notify  } from "@/utils";

import { useProfile } from "../-profile-context";

function Chip({
  active,
  onClick,
  children,
  isDay = true,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  isDay?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-[68px] h-[60px] rounded-md px-4 py-2 text-[16px] transition font-bold leading-7",
        "bg-fog text-garnet",
        active &&
          "bg-fog-dark ring-2 ring-bg-gradient-deep-blue shadow-[0_0_0_1px_rgba(184,134,11,0.25)]",
        !isDay && "text-left font-normal min-w-[174px]",
      )}
    >
      {children}
    </button>
  );
}

const DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;
const TIMES = ["Morning", "Afternoon", "Evening"] as const;

const AvailabilitySchema = z.object({
  days: z.array(z.string()),
  times: z.array(z.string()),
  available_distance: z.number().min(1).max(50).default(10),
});

type Props = {
  onContinue?: (handler: () => Promise<boolean>) => void;
};

const DAY_KEYS: Record<(typeof DAYS)[number], string> = {
  Mo: "availability.days.mo",
  Tu: "availability.days.tu",
  We: "availability.days.we",
  Th: "availability.days.th",
  Fr: "availability.days.fr",
  Sa: "availability.days.sa",
  Su: "availability.days.su",
};

const TIME_KEYS: Record<(typeof TIMES)[number], string> = {
  Morning: "availability.times.morning",
  Afternoon: "availability.times.afternoon",
  Evening: "availability.times.evening",
};

export function AvailabilityStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const { profile, setProfile } = useProfile();
  const updateProfileMutation = useUpdateProfileMutation();
  const { show } = useBanners();

  const form = useForm({
    resolver: zodResolver(AvailabilitySchema),
    defaultValues: { days: [], times: [], available_distance: 10 },
    mode: "onBlur",
  });

  // Populate days and times from profile data
  React.useEffect(() => {
    if (!profile) return;
    const extractedDays: Array<string> = [];
    const extractedTimes: Array<string> = [];

    const dayMap: Record<string, string> = {
      monday: "Mo",
      tuesday: "Tu",
      wednesday: "We",
      thursday: "Th",
      friday: "Fr",
      saturday: "Sa",
      sunday: "Su",
    };

    Object.keys(dayMap).forEach((dayName) => {
      const profileKey = `available_${dayName}`;
      const dayValue = (profile as any)[profileKey];
      if (dayValue && Array.isArray(dayValue) && dayValue.length > 0) {
        extractedDays.push(dayMap[dayName]);
        if (extractedTimes.length === 0) {
          extractedTimes.push(
            ...dayValue.map(
              (time: string) =>
                time.charAt(0).toUpperCase() + time.slice(1).toLowerCase(),
            ),
          );
        }
      }
    });

    form.reset({
      days: extractedDays,
      times: extractedTimes,
      available_distance: (profile as any).available_distance || 10,
    });
  }, [profile, form]);

  const days = form.watch("days");
  const times = form.watch("times");
  const miles = Number(form.watch("available_distance") ?? 10);

  const allSelected =
    days.length === DAYS.length && times.length === TIMES.length;

  const minMiles = 1;
  const maxMiles = 50;
  const stepMiles = 1;

  const percent = Math.round(
    ((Math.min(Math.max(miles, minMiles), maxMiles) - minMiles) /
      (maxMiles - minMiles)) *
      100,
  );

  function onDaysChange(next: Array<string>) {
    form.setValue("days", next, { shouldDirty: true });
  }

  function onTimesChange(next: Array<string>) {
    form.setValue("times", next, { shouldDirty: true });
  }

  function onSelectAll(selected: boolean) {
    if (selected) {
      onDaysChange([...DAYS]);
      onTimesChange([...TIMES]);
    } else {
      onDaysChange([]);
      onTimesChange([]);
    }
  }

  function onDistanceChange(next: number) {
    const v = Math.min(Math.max(next, minMiles), maxMiles);
    form.setValue("available_distance", v, { shouldDirty: true });
  }

  // Register continue handler
  React.useEffect(() => {
    if (onContinue) {
      onContinue(async () => {
        if (days.length === 0 || times.length === 0) {
          show({ kind: "error", message: t("availability.selectDayAndTime") });
          return false;
        }
        try {
          const dayMap: Record<string, string> = {
            Mo: "Monday",
            Tu: "Tuesday",
            We: "Wednesday",
            Th: "Thursday",
            Fr: "Friday",
            Sa: "Saturday",
            Su: "Sunday",
          };
          const availability_days: Record<string, Array<string>> = {};
          for (const day of days) {
            const dayName = `available_${dayMap[day].toLowerCase()}`;
            const dayTimes = times.map((time) => time.toUpperCase());
            availability_days[dayName] = dayTimes;
          }
          const updatedProfile: Profile = {
            ...(profile as Profile),
            ...availability_days,
            available_distance: miles,
          };
          const res = await updateProfileMutation.mutateAsync(updatedProfile);
          setProfile(res.profile);
          notify.success(t("availability.saved"));
          return true;
        } catch (error: any) {
          show({
            kind: "error",
            message: error?.message || t("availability.saveFailed"),
          });
          return false;
        }
      });
    }
  }, [
    onContinue,
    days,
    times,
    miles,
    profile,
    updateProfileMutation,
    setProfile,
    show,
    t,
  ]);

  return (
    <section className="w-full space-y-5">
      <h2 className="heading-1 mb-10">{t("availability.title")}</h2>

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="w-full">
          {/* Days */}
          <div className="flex flex-wrap gap-3">
            {DAYS.map((d) => (
              <Chip
                key={d}
                active={days.includes(d)}
                onClick={() =>
                  onDaysChange(
                    days.includes(d)
                      ? days.filter((x) => x !== d)
                      : [...days, d],
                  )
                }
              >
                {t(DAY_KEYS[d])}
              </Chip>
            ))}
          </div>

          {/* Times of day */}
          <div className="mt-4 flex flex-wrap gap-3">
            {TIMES.map((tm) => (
              <Chip
                key={tm}
                active={times.includes(tm)}
                onClick={() =>
                  onTimesChange(
                    times.includes(tm)
                      ? times.filter((x) => x !== tm)
                      : [...times, tm],
                  )
                }
                isDay={false}
              >
                {t(TIME_KEYS[tm])}
              </Chip>
            ))}
          </div>

          {/* Select all */}
          <label className="mt-4 flex items-center gap-2 text-garnet">
            <input
              type="checkbox"
              className="size-4 accent-[#511A10]"
              checked={allSelected}
              onChange={(e) => {
                onSelectAll(e.target.checked);
              }}
            />
            <span className="body-small">{t("availability.selectAll")}</span>
          </label>

          <hr className="my-6 border-garnet/20" />

          {/* Distance slider */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="body-small text-garnet">
                {t("availability.locationWithin")}
              </span>
              <span className="body-small text-garnet">
                {t("availability.miles", { count: miles })}
              </span>
            </div>

            <div className="rounded-md bg-[#f5f3f2] px-3 py-3">
              {/* Slider stack */}
              <div className="relative h-6 select-none">
                {/* base track (garnet line) */}
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-garnet/90" />

                {/* gold fill up to thumb */}
                <div
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-2 rounded-full"
                  style={{
                    width: `${percent}%`,
                    // exact Figma gold via your CSS variable gradient
                    backgroundImage: "var(--pm-gradient-gold-2)",
                  }}
                />

                {/* actual range input overlays everything, but is visually styled */}
                <input
                  type="range"
                  min={minMiles}
                  max={maxMiles}
                  step={stepMiles}
                  value={miles}
                  onChange={(e) => onDistanceChange(Number(e.target.value))}
                  aria-label={t("availability.distanceLabel")}
                  className={`
          absolute inset-0 w-full appearance-none bg-transparent outline-none

          /* Track: transparent (we draw tracks with the two <div>s above) */
          [&::-webkit-slider-runnable-track]:h-2
          [&::-webkit-slider-runnable-track]:bg-transparent
          [&::-moz-range-track]:h-2
          [&::-moz-range-track]:bg-transparent
          [&::-ms-track]:h-2
          [&::-ms-track]:bg-transparent

          /* Thumb: 21x21 with your gradient SVG */
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:h-[21px]
          [&::-webkit-slider-thumb]:w-[21px]
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-no-repeat
          [&::-webkit-slider-thumb]:bg-center
          [&::-webkit-slider-thumb]:bg-contain
          [&::-webkit-slider-thumb]:bg-[url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='21' height='21' viewBox='0 0 21 21' fill='none'><circle cx='10.5' cy='10.5' r='10.5' transform='rotate(-180 10.5 10.5)' fill='url(%23g)'/><defs><linearGradient id='g' x1='0' y1='10.5' x2='19.8772' y2='9.36643' gradientUnits='userSpaceOnUse'><stop stop-color='%23FBC468'/><stop offset='1' stop-color='%238F5600'/></linearGradient></defs></svg>")]
          /* center thumb on 8px track (21px thumb -> ~-6.5px, -6 works well visually) */
          [&::-webkit-slider-thumb]:translate-y-[-6px]

          /* Firefox thumb */
          [&::-moz-range-thumb]:height-[21px]
          [&::-moz-range-thumb]:width-[21px]
          [&::-moz-range-thumb]:border:none
          [&::-moz-range-thumb]:border-radius:9999px
          [&::-moz-range-thumb]:background:no-repeat center/contain
          [&::-moz-range-thumb]:background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='21' height='21' viewBox='0 0 21 21' fill='none'><circle cx='10.5' cy='10.5' r='10.5' transform='rotate(-180 10.5 10.5)' fill='url(%23g)'/><defs><linearGradient id='g' x1='0' y1='10.5' x2='19.8772' y2='9.36643' gradientUnits='userSpaceOnUse'><stop stop-color='%23FBC468'/><stop offset='1' stop-color='%238F5600'/></linearGradient></defs></svg>")
        `}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}
